import React from 'react';
import { formatLocalizedDate } from '../../../utils/format-date';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import parse from 'html-react-parser';
import Image from 'next/image';
import MoreStories from '../MoreStories';
import appUtils from '../../../utils/appUtils';
import { logEvent } from '../../../lib/gtag';
import { Separator } from '@lb-service/ui';

import FacebookIconSmall from '../../../../public/images/facebook-icon-small.svg';
import FacebookIcon from '../../../../public/images/facebook-icon.svg';
import TwitterIconSmall from '../../../../public/images/twitter-icon-small.svg';
import TwitterIcon from '../../../../public/images/twitter-icon.svg';

const Single = ({
  content,
  slug,
  tags,
  category,
  title,
  avatarUrl,
  authorName,
  authorSlug,
  authorDescription,
  thumbnail,
  date,
  morePosts,
}) => {
  const router = useRouter();
  const { locale } = router;
  const { t } = useTranslation('blog');
  const avatarPart = (
    <Image
      priority
      src={avatarUrl}
      alt="Author Avatar"
      width={40}
      height={40}
      className="rounded-full"
    />
  );
  const baseUrl = appUtils.getSiteUrl(locale);
  const permalink = `${baseUrl}/blog/${slug}`;
  let bannerUrl;
  switch (locale) {
    case 'ja':
      bannerUrl = '/images/blog_single_banner_ja.png';
      break;
    case 'es':
      bannerUrl = '/images/blog_single_banner_es.png';
      break;
    case 'pt':
      bannerUrl = '/images/blog_single_banner_pt.png';
      break;
    default:
      bannerUrl = '/images/blog_single_banner_en.png';
      break;
  }
  const parsedContent = parse(content, {
    replace: (node: any) => {
      if (node.name === 'br') {
        return <div className="my-6" />;
      }
      // if (node.name === 'img' && node.attribs) {
      //   const props = {
      //     // ...attributesToProps(node.attribs),
      //     layout: 'responsive',
      //     // loading: 'lazy',
      //     src: node.attribs.src,
      //     alt: 'attached image',
      //   };
      //   return <Image {...props} />;
      // }
    },
  });
  const tagsPart = tags.map((tag) => {
    return (
      <li key={`tag-${tag.node.slug}`}>
        <Link href="/blog/tag/[tag]" as={`/blog/tag/${tag.node.slug}`}>
          {tag.node.name}
        </Link>
      </li>
    );
  });

  const handleClickBanner = () => {
    logEvent('Display', 'Clicked a Display', 'blogtracker');
  };

  return (
    <div className="bg-white py-10 px-4">
      <div className="blog-article max-w-3xl mx-auto px-4 sm:px-6 lg:max-w-7xl lg:px-8">
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12 single-post">
            <div className="single-post--header">
              <h1 className="headline">{title}</h1>
              <div className="single-post--metadata flex justify-between">
                <div className="published-by">
                  <span className="single-post--author-image mr-2">
                    {avatarPart}
                  </span>
                  <span className="single-post--author-name">
                    By {authorName}
                  </span>
                  <span className="single-post--posted-at">
                    on {formatLocalizedDate(date, locale)}
                  </span>
                </div>
                <div className="flex items-center">
                  <div className="category-wrapper hidden sm:block mr-2">
                    <span className="category">
                      <Link
                        href={'/blog/category/[category]'}
                        as={`/blog/category/${category.slug}`}
                      >
                        {category.name}
                      </Link>
                    </span>
                  </div>
                  <ul className="sm:items-center hidden sm:flex">
                    <li className="facebook mr-2">
                      <a
                        target="_blank"
                        href={`https://www.facebook.com/dialog/feed?&app_id=1031492147011448&display=popup&link=${permalink}&redirect_uri=${permalink}`}
                        rel="noreferrer"
                      >
                        <FacebookIconSmall />
                      </a>
                    </li>
                    <li className="twitter">
                      <a
                        target="_blank"
                        href={`https://twitter.com/intent/tweet?text=${title}%20${permalink}%20%23codegrit`}
                        rel="noreferrer"
                      >
                        <TwitterIconSmall />
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="clear-both"></div>
              <div className="category-wrapper block sm:hidden">
                <span className="category">
                  <Link
                    href={'/blog/category/[category]'}
                    as={`/blog/category/${category.slug}`}
                  >
                    {category.name}
                  </Link>
                </span>
              </div>
              <div className="main-image-wrapper auto">
                <Image
                  priority
                  width={1024}
                  height={768}
                  sizes="100vw"
                  style={{ width: '100%', height: 'auto' }}
                  src={thumbnail}
                  alt="Blog Article Thumbnail"
                />
              </div>
            </div>
            <div className="w-full max-w-[760px] mx-auto single-post--main">
              {parsedContent}

              <div className="blog-banner-wrap">
                <a
                  href={appUtils.getAppPath(locale, 'tutors')}
                  onClick={handleClickBanner}
                >
                  <Image
                    priority
                    width={760}
                    height={289}
                    src={bannerUrl}
                    alt="Display link to browsing tutors"
                  />
                </a>
              </div>
              <Separator />
              <div className="post-end-share-btns mt-8 mb-8">
                <a
                  tabIndex={0}
                  className="share-btn svc-facebook"
                  target="_blank"
                  href={`https://www.facebook.com/dialog/feed?&app_id=1031492147011448&display=popup&link=${permalink}&redirect_uri=${permalink}`}
                  rel="noreferrer"
                >
                  <span
                    className="at-icon-wrapper"
                    style={{
                      backgroundColor: 'rgb(59, 89, 152)',
                      marginRight: 5,
                    }}
                  >
                    <FacebookIcon className="at-icon at-icon-facebook" />
                    <span className="at-label" title="Facebook">
                      {t('shareFacebook')}
                    </span>
                  </span>
                </a>
                <a
                  tabIndex={0}
                  className="share-btn svc-twitter"
                  target="_blank"
                  href={`https://twitter.com/intent/tweet?text=${title}%20${permalink}%20%23codegrit`}
                  rel="noreferrer"
                >
                  <span
                    className="at-icon-wrapper"
                    style={{
                      backgroundColor: 'rgb(29, 161, 242)',
                    }}
                  >
                    <TwitterIcon className="at-icon at-icon-twitter" />
                    <span className="at-label" title="Twitter">
                      {t('shareTwitter')}
                    </span>
                  </span>
                </a>
              </div>
              <ul className="tag-bar mb-8">{tagsPart}</ul>
              <div className="author-section mb-8">
                <h5>{t('author')}</h5>
                <div className="author-bio flex">
                  <div className="author-avatar">
                    <Image
                      height={120}
                      width={120}
                      src={avatarUrl}
                      className="rounded-full"
                      alt="Author Avatar"
                    />
                  </div>
                  <div className="author-details ml-4">
                    <div className="author-details--name mb-4">
                      <Link
                        as={`/blog/author/${authorSlug}`}
                        href="/blog/author/[author]"
                      >
                        {authorName}
                      </Link>
                    </div>
                    {authorDescription}
                  </div>
                </div>
              </div>
              {morePosts.length > 0 && <MoreStories posts={morePosts} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Single;
